import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  TruckIcon,
  MapPinIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapIcon,
  FunnelIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/hooks/useAuth'
import { getTripStats, getTrips, getTripSessions, type Trip, type TripSession } from '@/api/tripsApi'
import { getFleetStats, getVehicles, type Vehicle } from '@/api/fleetApi'
import { getRouteStats, getRoutes, type TransportRoute } from '@/api/routesApi'
import { getInstitutions, type Institution } from '@/api/institutionsApi'
import ExportModal from '@/components/ui/ExportModal'
import { type ExportConfig } from '@/utils/exportUtils'
import toast from 'react-hot-toast'

interface TripStats {
  totalTrips: number
  todaysTrips: number
  liveTrips: number
  completedTrips: number
  scheduled: number
  inProgress: number
  completed: number
  cancelled: number
  delayed: number
}

interface FleetStats {
  totalVehicles: number
  activeTrips: number
  maintenanceDue: number
  documentsExpiring: number
  activeVehicles: number
  maintenanceVehicles: number
  retiredVehicles: number
  outOfServiceVehicles: number
  buses: number
  vans: number
  cars: number
  minibuses: number
}

interface RouteStats {
  totalRoutes: number
  activeRoutes: number
  routesWithVehicles: number
  routesWithDrivers: number
  routesWithoutVehicles: number
  routesWithoutDrivers: number
  totalStops: number
  averageStopsPerRoute: number
}

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899']

const TransportReportsPage: React.FC = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [tripStats, setTripStats] = useState<TripStats | null>(null)
  const [fleetStats, setFleetStats] = useState<FleetStats | null>(null)
  const [routeStats, setRouteStats] = useState<RouteStats | null>(null)
  const [trips, setTrips] = useState<Trip[]>([])
  const [sessions, setSessions] = useState<TripSession[]>([])
  const [vehicles, setVehicles] = useState<Vehicle[]>([])
  const [routes, setRoutes] = useState<TransportRoute[]>([])
  const [institutions, setInstitutions] = useState<Institution[]>([])

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date()
    date.setMonth(date.getMonth() - 1)
    return date.toISOString().split('T')[0]
  })
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])
  const [selectedInstitution, setSelectedInstitution] = useState<string>('')
  const [showExportModal, setShowExportModal] = useState(false)

  const isSuperAdmin = user?.role === 'SUPER_ADMIN'

  useEffect(() => {
    fetchData()
    if (isSuperAdmin) {
      fetchInstitutions()
    }
  }, [startDate, endDate, selectedInstitution])

  const fetchInstitutions = async () => {
    try {
      const response = await getInstitutions()
      if (Array.isArray(response)) {
        setInstitutions(response)
      } else if (response?.institutions) {
        setInstitutions(response.institutions)
      }
    } catch (error) {
      console.error('Failed to fetch institutions:', error)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const institutionId = isSuperAdmin ? selectedInstitution || undefined : undefined

      const [
        tripStatsRes,
        fleetStatsRes,
        routeStatsRes,
        tripsRes,
        sessionsRes,
        vehiclesRes,
        routesRes,
      ] = await Promise.all([
        getTripStats(institutionId),
        getFleetStats(institutionId),
        getRouteStats(institutionId),
        getTrips({ limit: 100, institutionId }),
        getTripSessions({ limit: 100, institutionId }),
        getVehicles({ limit: 100, institutionId }),
        getRoutes({ institutionId }),
      ])

      setTripStats(tripStatsRes.data || tripStatsRes)
      setFleetStats(fleetStatsRes.data || fleetStatsRes)
      setRouteStats(routeStatsRes.data || routeStatsRes)
      setTrips(tripsRes.data?.trips || tripsRes.trips || [])
      setSessions(sessionsRes.data?.sessions || sessionsRes.sessions || [])
      setVehicles(vehiclesRes.data?.vehicles || vehiclesRes.vehicles || [])
      setRoutes(routesRes.data?.routes || routesRes.routes || routesRes.data || [])
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to load transport data')
    } finally {
      setLoading(false)
    }
  }

  // Generate chart data
  const tripStatusData = tripStats ? [
    { name: 'Scheduled', value: tripStats.scheduled || 0, color: '#3B82F6' },
    { name: 'In Progress', value: tripStats.inProgress || 0, color: '#10B981' },
    { name: 'Completed', value: tripStats.completed || 0, color: '#6B7280' },
    { name: 'Cancelled', value: tripStats.cancelled || 0, color: '#EF4444' },
    { name: 'Delayed', value: tripStats.delayed || 0, color: '#F59E0B' },
  ].filter(d => d.value > 0) : []

  const vehicleTypeData = fleetStats ? [
    { name: 'Buses', value: fleetStats.buses || 0, color: '#2563EB' },
    { name: 'Vans', value: fleetStats.vans || 0, color: '#10B981' },
    { name: 'Minibuses', value: fleetStats.minibuses || 0, color: '#F59E0B' },
    { name: 'Cars', value: fleetStats.cars || 0, color: '#8B5CF6' },
  ].filter(d => d.value > 0) : []

  const vehicleStatusData = fleetStats ? [
    { name: 'Active', value: fleetStats.activeVehicles || 0, color: '#10B981' },
    { name: 'Maintenance', value: fleetStats.maintenanceVehicles || 0, color: '#F59E0B' },
    { name: 'Out of Service', value: fleetStats.outOfServiceVehicles || 0, color: '#EF4444' },
    { name: 'Retired', value: fleetStats.retiredVehicles || 0, color: '#6B7280' },
  ].filter(d => d.value > 0) : []

  // Generate daily trip data for the last 7 days
  const generateDailyTripData = () => {
    const data = []
    for (let i = 6; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dateStr = date.toISOString().split('T')[0]
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })

      // Filter sessions for this day
      const daySessions = sessions.filter(s => s.startTime?.split('T')[0] === dateStr)

      data.push({
        day: dayName,
        date: dateStr,
        trips: daySessions.length,
        completed: daySessions.filter(s => s.status === 'completed').length,
        cancelled: daySessions.filter(s => s.status === 'cancelled').length,
      })
    }
    return data
  }

  const dailyTripData = generateDailyTripData()

  // Route performance data
  const routePerformanceData = routes.slice(0, 8).map(route => ({
    name: route.routeNumber || route.routeName?.substring(0, 10) || 'Unknown',
    stops: route.stops?.length || 0,
    vehicles: route.assignedVehicleIds?.length || 0,
    drivers: route.assignedDriverIds?.length || 0,
  }))

  // Trip type distribution
  const tripTypeData = () => {
    const types: Record<string, number> = {}
    trips.forEach(trip => {
      const type = trip.tripType || 'unknown'
      types[type] = (types[type] || 0) + 1
    })
    return Object.entries(types).map(([name, value], index) => ({
      name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value,
      color: COLORS[index % COLORS.length],
    }))
  }

  // Export config
  const getExportConfig = (): ExportConfig => {
    const selectedInstitutionName = institutions.find(i => i.id === selectedInstitution)?.name

    return {
      title: 'Transport Report',
      subtitle: selectedInstitutionName ? `Institution: ${selectedInstitutionName}` : 'All Institutions',
      filename: `transport-report-${startDate}-to-${endDate}`,
      institution: {
        name: selectedInstitutionName || 'SmartTag School',
      },
      dateRange: {
        from: startDate,
        to: endDate,
      },
      generatedAt: new Date().toLocaleString(),
      summary: [
        { label: 'Total Trips', value: tripStats?.totalTrips || 0 },
        { label: 'Live Trips', value: tripStats?.liveTrips || 0 },
        { label: 'Total Vehicles', value: fleetStats?.totalVehicles || 0 },
        { label: 'Active Vehicles', value: fleetStats?.activeVehicles || 0 },
        { label: 'Total Routes', value: routeStats?.totalRoutes || 0 },
        { label: 'Active Routes', value: routeStats?.activeRoutes || 0 },
      ],
      sections: [
        {
          title: 'Trip Statistics',
          columns: [
            { header: 'Metric', key: 'metric', width: 35 },
            { header: 'Value', key: 'value', width: 20 },
          ],
          data: [
            { metric: 'Total Trips', value: tripStats?.totalTrips || 0 },
            { metric: "Today's Trips", value: tripStats?.todaysTrips || 0 },
            { metric: 'Live Trips', value: tripStats?.liveTrips || 0 },
            { metric: 'Completed', value: tripStats?.completed || 0 },
            { metric: 'In Progress', value: tripStats?.inProgress || 0 },
            { metric: 'Scheduled', value: tripStats?.scheduled || 0 },
            { metric: 'Cancelled', value: tripStats?.cancelled || 0 },
            { metric: 'Delayed', value: tripStats?.delayed || 0 },
          ],
        },
        {
          title: 'Fleet Statistics',
          columns: [
            { header: 'Metric', key: 'metric', width: 35 },
            { header: 'Value', key: 'value', width: 20 },
          ],
          data: [
            { metric: 'Total Vehicles', value: fleetStats?.totalVehicles || 0 },
            { metric: 'Active Vehicles', value: fleetStats?.activeVehicles || 0 },
            { metric: 'In Maintenance', value: fleetStats?.maintenanceVehicles || 0 },
            { metric: 'Out of Service', value: fleetStats?.outOfServiceVehicles || 0 },
            { metric: 'Documents Expiring', value: fleetStats?.documentsExpiring || 0 },
            { metric: 'Buses', value: fleetStats?.buses || 0 },
            { metric: 'Vans', value: fleetStats?.vans || 0 },
            { metric: 'Cars', value: fleetStats?.cars || 0 },
            { metric: 'Minibuses', value: fleetStats?.minibuses || 0 },
          ],
        },
        {
          title: 'Route Statistics',
          columns: [
            { header: 'Metric', key: 'metric', width: 35 },
            { header: 'Value', key: 'value', width: 20 },
          ],
          data: [
            { metric: 'Total Routes', value: routeStats?.totalRoutes || 0 },
            { metric: 'Active Routes', value: routeStats?.activeRoutes || 0 },
            { metric: 'Routes with Vehicles', value: routeStats?.routesWithVehicles || 0 },
            { metric: 'Routes without Vehicles', value: routeStats?.routesWithoutVehicles || 0 },
            { metric: 'Routes with Drivers', value: routeStats?.routesWithDrivers || 0 },
            { metric: 'Routes without Drivers', value: routeStats?.routesWithoutDrivers || 0 },
            { metric: 'Total Stops', value: routeStats?.totalStops || 0 },
            { metric: 'Avg Stops per Route', value: routeStats?.averageStopsPerRoute?.toFixed(1) || 0 },
          ],
        },
        {
          title: 'Daily Trip Activity',
          columns: [
            { header: 'Day', key: 'day', width: 25 },
            { header: 'Total Trips', key: 'trips', width: 20 },
            { header: 'Completed', key: 'completed', width: 20 },
            { header: 'Cancelled', key: 'cancelled', width: 20 },
          ],
          data: dailyTripData,
        },
        {
          title: 'Trip Type Distribution',
          columns: [
            { header: 'Type', key: 'name', width: 30 },
            { header: 'Count', key: 'value', width: 20 },
          ],
          data: tripTypeData(),
        },
        {
          title: 'Fleet by Vehicle Type',
          columns: [
            { header: 'Vehicle Type', key: 'name', width: 30 },
            { header: 'Count', key: 'value', width: 20 },
          ],
          data: [
            { name: 'Buses', value: fleetStats?.buses || 0 },
            { name: 'Vans', value: fleetStats?.vans || 0 },
            { name: 'Cars', value: fleetStats?.cars || 0 },
            { name: 'Minibuses', value: fleetStats?.minibuses || 0 },
          ].filter(item => item.value > 0),
        },
        {
          title: 'Recent Trip Sessions',
          columns: [
            { header: 'Date', key: 'date', width: 25 },
            { header: 'Status', key: 'status', width: 20 },
            { header: 'Start Time', key: 'startTime', width: 25 },
          ],
          data: sessions.slice(0, 20).map(session => ({
            date: new Date(session.createdAt).toLocaleDateString(),
            status: session.status || 'N/A',
            startTime: session.startTime ? new Date(session.startTime).toLocaleTimeString() : 'Not started',
          })),
        },
      ],
    }
  }

  

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transport Reports</h1>
          <p className="text-gray-600 mt-1">
            Fleet performance, trip analytics, and route insights
          </p>
        </div>
        <Button onClick={() => setShowExportModal(true)}>
          <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Filters */}
<div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
  <div className="flex flex-wrap md:flex-nowrap items-center md:items-end gap-4">

    {/* Period Selector */}
    <div className="flex flex-col md:flex-row md:items-center gap-2">
      <div className="flex items-center gap-2">
        <CalendarDaysIcon className="h-5 w-5 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">Period:</span>
      </div>

      <div className="flex items-center gap-2">
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="w-auto"
        />
        <span className="text-gray-500">to</span>
        <Input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-auto"
        />
      </div>
    </div>

    {/* Institution Selector */}
    {isSuperAdmin && (
      <>
        <div className="hidden md:block h-6 w-px bg-gray-300" />

        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex items-center gap-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Institution:</span>
          </div>

          <select
            value={selectedInstitution}
            onChange={(e) => setSelectedInstitution(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-full md:w-auto"
          >
            <option value="">All Institutions</option>
            {institutions.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.name}
              </option>
            ))}
          </select>
        </div>
      </>
    )}
  </div>
</div>


      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Trip Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapIcon className="h-6 w-6 text-blue-600" />
            </div>
            {tripStats && tripStats.todaysTrips > 0 && (
              <span className="flex items-center text-sm text-green-600">
                <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                {tripStats.todaysTrips} today
              </span>
            )}
          </div>
          <p className="mt-4 text-2xl font-bold text-gray-900">{tripStats?.totalTrips || 0}</p>
          <p className="text-sm text-gray-500">Total Trips</p>
        </motion.div>

        {/* Fleet Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-green-100 rounded-lg">
              <TruckIcon className="h-6 w-6 text-green-600" />
            </div>
            {fleetStats && fleetStats.maintenanceDue > 0 && (
              <span className="flex items-center text-sm text-yellow-600">
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                {fleetStats.maintenanceDue} due
              </span>
            )}
          </div>
          <p className="mt-4 text-2xl font-bold text-gray-900">{fleetStats?.totalVehicles || 0}</p>
          <p className="text-sm text-gray-500">Total Vehicles</p>
        </motion.div>

        {/* Route Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MapPinIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
          <p className="mt-4 text-2xl font-bold text-gray-900">{routeStats?.totalRoutes || 0}</p>
          <p className="text-sm text-gray-500">Total Routes</p>
          <p className="text-xs text-gray-400 mt-1">{routeStats?.totalStops || 0} stops</p>
        </motion.div>

        {/* Active/Live */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-5"
        >
          <div className="flex items-center justify-between">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-orange-600" />
            </div>
            {tripStats && tripStats.liveTrips > 0 && (
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
            )}
          </div>
          <p className="mt-4 text-2xl font-bold text-gray-900">{tripStats?.liveTrips || 0}</p>
          <p className="text-sm text-gray-500">Live Trips</p>
        </motion.div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trip Trends */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Trip Activity</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={dailyTripData}>
                <defs>
                  <linearGradient id="colorTrips" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="day" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="trips"
                  name="Total Trips"
                  stroke="#2563EB"
                  fill="url(#colorTrips)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="completed"
                  name="Completed"
                  stroke="#10B981"
                  fill="url(#colorCompleted)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Trip Status Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trip Status Distribution</h3>
          <div className="h-72 flex items-center justify-center">
            {tripStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tripStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {tripStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center text-gray-500">
                <ChartBarIcon className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>No trip data available</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vehicle Type Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fleet by Type</h3>
          <div className="h-64">
            {vehicleTypeData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={vehicleTypeData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {vehicleTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No vehicle data
              </div>
            )}
          </div>
        </motion.div>

        {/* Vehicle Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fleet Status</h3>
          <div className="h-64">
            {vehicleStatusData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={vehicleStatusData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis type="number" stroke="#6B7280" fontSize={12} />
                  <YAxis type="category" dataKey="name" stroke="#6B7280" fontSize={12} width={80} />
                  <Tooltip />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {vehicleStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No status data
              </div>
            )}
          </div>
        </motion.div>

        {/* Trip Type Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Trip Types</h3>
          <div className="h-64">
            {tripTypeData().length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={tripTypeData()}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {tripTypeData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No trip type data
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Route Performance */}
      {routePerformanceData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Route Overview</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={routePerformanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} />
                <Tooltip />
                <Legend />
                <Bar dataKey="stops" name="Stops" fill="#2563EB" radius={[4, 4, 0, 0]} />
                <Bar dataKey="vehicles" name="Vehicles" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="drivers" name="Drivers" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Completion Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {tripStats && tripStats.totalTrips > 0
                  ? Math.round(((tripStats.completed || 0) / tripStats.totalTrips) * 100)
                  : 0}%
              </p>
              <p className="text-sm text-gray-500">Trip Completion Rate</p>
            </div>
          </div>
        </div>

        {/* Fleet Availability */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TruckIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {fleetStats && fleetStats.totalVehicles > 0
                  ? Math.round(((fleetStats.activeVehicles || 0) / fleetStats.totalVehicles) * 100)
                  : 0}%
              </p>
              <p className="text-sm text-gray-500">Fleet Availability</p>
            </div>
          </div>
        </div>

        {/* Routes Coverage */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <MapPinIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {routeStats && routeStats.totalRoutes > 0
                  ? Math.round(((routeStats.routesWithVehicles || 0) / routeStats.totalRoutes) * 100)
                  : 0}%
              </p>
              <p className="text-sm text-gray-500">Routes with Vehicles</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {(fleetStats?.maintenanceDue || 0) + (fleetStats?.documentsExpiring || 0)}
              </p>
              <p className="text-sm text-gray-500">Attention Required</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Sessions Table */}
      {sessions.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Trip Sessions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Session
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Trip
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Students
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Start Time
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sessions.slice(0, 10).map((session) => (
                  <tr key={session.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{session.sessionName}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.trip?.tripName || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        session.status === 'completed' ? 'bg-green-100 text-green-800' :
                        session.status === 'active' ? 'bg-blue-100 text-blue-800' :
                        session.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {session.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.studentAttendance?.length || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.startTime ? new Date(session.startTime).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        config={getExportConfig()}
        title="Export Transport Report"
      />
    </div>
  )
}

export default TransportReportsPage
